import { useCallback, useEffect, useState } from "react";

/**
 * Tracks whether the end of a scrolling region has been reached, latching once
 * it has.
 *
 * Watches a sentinel at the end of the content rather than comparing
 * `scrollTop + clientHeight` against `scrollHeight`. That arithmetic needs a
 * tolerance to survive sub-pixel rounding and browser zoom, and it has to be
 * recomputed whenever the container resizes. A sentinel gets all of that from
 * the browser, and - the case most worth having - content shorter than the
 * container reports as reached immediately, because the sentinel is already
 * visible. Otherwise a short policy could never be accepted.
 *
 * Latches deliberately: scrolling back up does not un-read a document.
 *
 * The refs are callback refs rather than `useRef` objects because the caller
 * may be inside a portal that mounts after its parent's first effect - MUI's
 * Modal does exactly that - so an effect keyed on `[]` runs while both nodes
 * are still null. Callback refs re-run the effect when the nodes actually
 * attach, and "not attached yet" is treated as *not* reached, so mounting
 * cannot be mistaken for having read the document.
 *
 * Fails open only when `IntersectionObserver` is unavailable. A gate that
 * cannot be satisfied is worse than no gate - it would lock the user out of
 * the product with no way forward but signing out.
 */
export function useScrolledToEnd() {
  const [root, setRoot] = useState<Element | null>(null);
  const [sentinel, setSentinel] = useState<Element | null>(null);
  const [reachedEnd, setReachedEnd] = useState(false);

  const rootRef = useCallback((node: Element | null) => setRoot(node), []);
  const sentinelRef = useCallback(
    (node: Element | null) => setSentinel(node),
    []
  );

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setReachedEnd(true);
      return;
    }
    if (!root || !sentinel) {
      // Still mounting. Deliberately not treated as reached.
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setReachedEnd(true);
        }
      },
      { root }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [root, sentinel]);

  return { rootRef, sentinelRef, reachedEnd };
}

export default useScrolledToEnd;
